/**
 * ConversationSummary Utility Functions
 * Pure functions for formatting and processing summary content
 */

import { tChatEvent } from '../Chat.types';

/**
 * Summary content markers that indicate a conversation summary
 * Organized by format type for better maintainability
 */
const SUMMARY_CONTENT_MARKERS = [
  '**📊 Estado actual:**',
  '**🎯 Puntos clave:**',
  '**✅ Próximos pasos:**',
  '**⚠️ Alertas:**',
  '**🎯 Estado del Lead:**',
  '**👤 Perfil del Cliente:**',
  '**🔍 Temas Principales:**',
  '**✅ Próximos Pasos Recomendados:**',
  'Puntos Principales Discutidos',
  'Cliente insatisfecho',
  'Cliente potencial',
  'servicio recibido',
] as const;

/**
 * Detects if the AI event is a conversation summary
 * Supports multiple summary formats (new and legacy)
 */
export function isSummaryEvent(aiEvent: tChatEvent): boolean {
  // Title-based detection (when backend properly sets titles)
  const titleCheck =
    aiEvent?.note?.title?.includes('Resumen de Conversación') || false;
  const emojiTitleCheck =
    aiEvent?.note?.title?.includes('📝 Resumen de Conversación') || false;

  // Content-based detection for multiple summary formats
  const content = aiEvent?.content?.text || '';
  const hasSummaryMarkers = SUMMARY_CONTENT_MARKERS.some((marker) =>
    content.includes(marker),
  );

  const hasHeuristicPattern =
    content.length > 200 &&
    content.includes('Cliente') &&
    (content.includes('servicio') ||
      content.includes('producto') ||
      content.includes('consulta'));

  const contentCheck = hasSummaryMarkers || hasHeuristicPattern;

  return titleCheck || emojiTitleCheck || contentCheck;
}

/**
 * Formats markdown-like content to HTML
 * Converts **bold** to <strong> and line breaks to <br>
 */
export function formatSummaryContent(content: string): string {
  // Escape existing HTML to prevent XSS
  const escapeHtml = (str: string) => {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  };

  const escaped = escapeHtml(content);
  return escaped
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n/g, '<br>');
}

/**
 * Formats timestamp to readable time
 */
export function formatTime(timestamp: string): string {
  return new Date(timestamp).toLocaleTimeString('es-ES', {
    hour: '2-digit',
    minute: '2-digit',
  });
}
